import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  ReactNode,
  useMemo,
} from 'react';
import useStopwatchTimer from '../hooks/useStopwatchTimer';
import useSegmentTracking from '../hooks/useSegmentTracking';
import useWorkoutSessionState, { LapData } from '../hooks/useWorkoutSessionState';
import { DEFAULTS } from '../constants';
import { createWorkoutHandlers } from './StopwatchWorkoutHandlers';
import { Segment, SegmentsByLap } from '../types/segment';
import { WorkoutSession } from '../types/workout';
import { StopwatchRef, Lap } from '../types/stopwatch';

// Define interface for the context value
interface StopwatchContextValue {
  // Timer values and state
  /** Main stopwatch time in milliseconds */
  mainTime: number;
  /** Current lap time in milliseconds */
  lapTime: number;
  /** Current segment time in milliseconds */
  segmentTime: number;
  /** Whether the stopwatch is currently running */
  isRunning: boolean;
  /** Array of recorded laps with timing information */
  laps: Lap[];

  // Timer refs
  /** Reference to the main workout timer component */
  workoutTimerRef: React.RefObject<StopwatchRef | null>;

  // Timer actions
  /** Start all timers (main, lap, segment) */
  startTimer: () => void;
  /** Pause all timers */
  pauseTimer: () => void;
  /** Reset all timers and clear lap data */
  resetTimer: () => void;
  /** Record a lap and reset segment timer */
  recordLap: (customCallback?: (lap: Lap) => void) => Lap | null;
  /** Reset all timers and immediately start them */
  resetAndStart: () => void;
  /** Complete the current workout session */
  completeWorkout: () => void;

  // Segment tracking
  /** Distance of the current segment in meters */
  currentSegmentDistance: number | null;
  /** Pace of the last completed segment (min/unit) */
  lastSegmentPace: number | null;
  /** Current lap pace (min/unit) of the forming lap */
  currentLapPace: number | null;
  /** Pace of the last completed lap (min/unit) */
  lastCompletedLapPace: number | null;
  /** Total distance in meters */
  totalDistance: number;
  /** Map of segments organized by lap number */
  effectiveSegmentsByLap: SegmentsByLap;
  /** Record a new segment with the given distance */
  recordSegment: (distance: number) => { segment: Segment | null; lapPace: number | null };
  /** Calculate pace from time and distance */
  calculatePace: (time: number, distance: number) => number;
  /** Format a pace value as a readable string */
  formatPace: (pace: number | null) => string;
  /** Reset lap data while preserving pace information - needed by DistanceButtons */
  resetLapDataPreservingPace: (completedLapPace?: number | null) => void;
  /** Set the pace of the last completed segment - needed by DistanceButtons */
  setLastSegmentPace: React.Dispatch<React.SetStateAction<number | null>>;
  /** Set the pace of the last completed lap - needed by DistanceButtons */
  setLastCompletedLapPace: React.Dispatch<React.SetStateAction<number | null>>;

  // Workout session
  /** Array of all workout sessions */
  workoutSessions: WorkoutSession[];
  /** Current session ID */
  currentSessionId: string | null;
  /** Current session object */
  currentSession: WorkoutSession | null;
  /** Current lap number */
  currentLapNumber: number;
  /** The last workout session that was completed */
  lastCompletedSession: WorkoutSession | null;

  // Workout session actions
  /** Start a new workout session */
  startWorkout: () => string;
  /** Add a completed lap to the current session */
  addCompletedLapToSession: (lapData: Lap) => void;
  /** Add a segment to the current session */
  addSegmentToSession: (segmentData: Segment) => boolean;

  // UI state
  /** Whether the app is in review mode (viewing past workouts) */
  isReviewMode: boolean;
  /** Whether auto-lap feature is enabled */
  isAutoLapEnabled: boolean;

  // UI actions
  /** Toggle between workout and review modes */
  toggleReviewMode: () => void;
  /** Toggle auto-lap feature on/off */
  toggleAutoLap: () => void;
  /** Reset auto-lap setting to disabled */
  resetAutoLap: () => void;
  /** Delete a workout session by ID */
  deleteWorkoutSession: (sessionId: string) => void;

  // Pace unit settings
  /** Current pace unit (mile or km) */
  paceUnit: 'mile' | 'km';
  /** Toggle between mile and km for pace calculations */
  togglePaceUnit: () => void;
}

// Create the context with null as default value, but typed
const StopwatchContext = createContext<StopwatchContextValue | null>(null);

// Define props interface for the StopwatchProvider
interface StopwatchProviderProps {
  children: ReactNode;
}

/**
 * Provider component for stopwatch state and functionality
 * @param props - Component props
 * @returns Provider component
 */
export const StopwatchProvider: React.FC<StopwatchProviderProps> = ({ children }) => {
  // Create refs to reference the timer methods

  // Track the current lap number
  const [currentLapNumber, setCurrentLapNumber] = useState<number>(DEFAULTS.INITIAL_LAP_NUMBER);
  // Store the auto-lap on distance toggle state
  const [isAutoLapEnabled, setIsAutoLapEnabled] = useState<boolean>(false);
  // Store the pace unit (miles or kilometers)
  const [paceUnit, setPaceUnit] = useState<'mile' | 'km'>('mile');

  // Use the workout session state hook directly
  const {
    workoutSessions,
    currentSessionId,
    currentSession,
    isReviewMode,
    createWorkoutSession,
    saveLapData,
    saveSegmentData,
    completeWorkoutSession,
    deleteSession,
    enterReviewMode,
    exitReviewMode,
    lastCompletedSession,
  } = useWorkoutSessionState();

  // Use the stopwatch timer hook for timer functionality
  const {
    mainTime,
    lapTime,
    segmentTime,
    isRunning,
    laps,
    recordLap: baseRecordLap,
    pauseTimer,
    resetTimer,
    recordFinalLap,
    resetAndStart,
    resetSegmentTimer,
  } = useStopwatchTimer();

  // Derive currentLapSegments directly from currentSession
  const currentLapSegments = useMemo(() => {
    return currentSession?.segments || {};
  }, [currentSession]);

  // Use segment tracking hook
  const {
    currentSegmentDistance,
    lastSegmentPace,
    totalDistance,
    currentLapPace,
    lastCompletedLapPace,
    effectiveSegmentsByLap,
    recordSegment: baseRecordSegment,
    calculatePace,
    formatPace,
    resetLapSegmentData,
    resetLapDataPreservingPace,
    setLastSegmentPace,
    setLastCompletedLapPace,
    convertPaceValues,
  } = useSegmentTracking({
    segmentTime,
    mainTime,
    isRunning,
    currentLapNumber,
    externalSegmentsByLap: currentLapSegments,
    resetSegmentTimer,
    onSegmentRecorded: saveSegmentData,
    paceUnit,
  });

  // Toggle between workout mode and review mode
  const toggleReviewMode = useCallback(() => {
    if (isReviewMode) {
      exitReviewMode();
    } else {
      enterReviewMode();
    }
  }, [isReviewMode, enterReviewMode, exitReviewMode]);

  // Toggle auto-lap feature
  const toggleAutoLap = useCallback(() => {
    setIsAutoLapEnabled(prevState => !prevState);
  }, []);

  // Reset auto-lap to off
  const resetAutoLap = useCallback(() => {
    setIsAutoLapEnabled(false);
  }, []);

  // Toggle between miles and kilometers for pace
  const togglePaceUnit = useCallback(() => {
    setPaceUnit(prevUnit => {
      const fromUnit = prevUnit;
      const toUnit = prevUnit === 'mile' ? 'km' : 'mile';

      // Call the conversion function directly
      if (convertPaceValues) {
        // Ensure function exists before calling
        convertPaceValues(fromUnit, toUnit);
      } else {
        console.warn('convertPaceValues function not available at toggle time');
      }

      return toUnit; // Return the new unit for setPaceUnit
    });
  }, [convertPaceValues]); // Add convertPaceValues to dependencies

  // Adapter function for saving laps
  const addCompletedLapAdapter = useCallback(
    (lapData: Lap) => {
      const lapDataForStorage: LapData = {
        number: lapData.number,
        time: lapData.time,
        totalTime: lapData.cumulativeTime || lapData.time,
        timestamp: new Date(lapData.timestamp).toISOString(),
        isFinalLap: !isRunning,
      };
      saveLapData(lapDataForStorage);
    },
    [saveLapData, isRunning]
  );

  // Adapter function for saving segments (returns boolean)
  const addSegmentToSessionAdapter = useCallback(
    (segmentData: Segment): boolean => {
      const result = saveSegmentData(segmentData);
      return !!result; // Convert Segment | null to boolean
    },
    [saveSegmentData]
  );

  // Create workout handlers
  const { startTimer, recordLap, completeWorkout, recordSegment } = createWorkoutHandlers({
    resetAndStart,
    resetLapSegmentData,
    resetLapDataPreservingPace,
    hookStartWorkout: createWorkoutSession,
    baseRecordLap,
    addCompletedLap: addCompletedLapAdapter,
    recordFinalLap,
    pauseTimer,
    hookCompleteWorkout: completeWorkoutSession,
    setCurrentLapNumber,
    baseRecordSegment,
    setLastSegmentPace,
    setLastCompletedLapPace,
    currentLapPace,
    isRunning,
    resetAutoLap,
    isAutoLapEnabled,
    paceUnit,
  });

  // Use the calculatePace and formatPace from the hook directly
  // No need for extra memoization with paceUnit here as the hook handles it
  const memoizedCalculatePace = calculatePace;
  const memoizedFormatPace = formatPace;

  // Define the context value with proper workoutTimerRef handling
  const workoutTimerRefProxy = useRef<StopwatchRef | null>(null);

  // Define the context value
  const contextValue: StopwatchContextValue = useMemo(
    () => ({
      // Timer values and state
      mainTime,
      lapTime,
      segmentTime,
      isRunning,
      laps,
      // Timer refs
      workoutTimerRef: workoutTimerRefProxy,
      // Timer actions
      startTimer,
      pauseTimer,
      resetTimer,
      recordLap,
      resetAndStart,
      completeWorkout,
      // Segment tracking
      currentSegmentDistance,
      lastSegmentPace,
      currentLapPace,
      lastCompletedLapPace,
      totalDistance,
      effectiveSegmentsByLap,
      recordSegment,
      calculatePace: memoizedCalculatePace,
      formatPace: memoizedFormatPace,
      resetLapDataPreservingPace,
      setLastSegmentPace,
      setLastCompletedLapPace,
      // Workout session
      workoutSessions,
      currentSessionId,
      currentSession,
      lastCompletedSession,
      currentLapNumber,
      // Workout session actions
      startWorkout: createWorkoutSession,
      addCompletedLapToSession: addCompletedLapAdapter,
      addSegmentToSession: addSegmentToSessionAdapter,
      // UI state
      isReviewMode,
      isAutoLapEnabled,
      // UI actions
      toggleReviewMode,
      toggleAutoLap,
      resetAutoLap,
      deleteWorkoutSession: deleteSession,
      // Pace unit settings
      paceUnit,
      togglePaceUnit,
    }),
    [
      // List all dependencies for the context value memoization
      mainTime,
      lapTime,
      segmentTime,
      isRunning,
      laps,
      workoutTimerRefProxy,
      startTimer,
      pauseTimer,
      resetTimer,
      recordLap,
      resetAndStart,
      completeWorkout,
      currentSegmentDistance,
      lastSegmentPace,
      currentLapPace,
      lastCompletedLapPace,
      totalDistance,
      effectiveSegmentsByLap,
      recordSegment,
      memoizedCalculatePace,
      memoizedFormatPace,
      resetLapDataPreservingPace,
      setLastSegmentPace,
      setLastCompletedLapPace,
      workoutSessions,
      currentSessionId,
      currentSession,
      lastCompletedSession,
      currentLapNumber,
      createWorkoutSession,
      isReviewMode,
      isAutoLapEnabled,
      toggleReviewMode,
      toggleAutoLap,
      resetAutoLap,
      deleteSession,
      paceUnit,
      togglePaceUnit,
      addCompletedLapAdapter,
      addSegmentToSessionAdapter,
    ]
  );

  return <StopwatchContext.Provider value={contextValue}>{children}</StopwatchContext.Provider>;
};

/**
 * Hook for accessing the stopwatch context
 * @returns Stopwatch context value
 * @throws {Error} If used outside of a StopwatchProvider
 */
export const useStopwatchContext = (): StopwatchContextValue => {
  const context = useContext(StopwatchContext);
  if (context === null) {
    throw new Error('useStopwatchContext must be used within a StopwatchProvider');
  }
  return context;
};

export default StopwatchContext;
